import { Link } from "@tanstack/react-router";
import { ArrowRight, CheckCircle } from "lucide-react";

import { SectionHeading } from "./SectionHeading";
import { CtaBand } from "./CtaBand";
import { SITE_URL, ogImageDefault, ogImageMeta } from "../lib/seo";
import { kostenDisclaimer } from "../lib/kosten-content";
import type { KostenDetail } from "../lib/kosten-detail-content";

interface KostenDetailPageProps {
  detail: KostenDetail;
}

export function KostenDetailPage({ detail }: KostenDetailPageProps) {
  return (
    <>
      <div className="py-14 sm:py-20 lg:py-28">
        <div className="container-tight">
          <Link
            to="/aanbouw-kosten"
            className="mx-auto flex w-fit items-center gap-1 text-sm font-semibold text-primary hover:text-primary/80"
          >
            <ArrowRight className="h-4 w-4 rotate-180" aria-hidden="true" />
            Terug naar aanbouw kosten
          </Link>

          <SectionHeading
            as="h1"
            eyebrow={detail.eyebrow}
            title={detail.h1}
            description={detail.description}
            align="center"
            className="mx-auto"
          />

          <div className="mx-auto mt-10 max-w-3xl space-y-14 sm:mt-14">
            <section>
              <h2 className="font-heading text-2xl font-bold tracking-tight text-foreground">
                Kort antwoord
              </h2>
              <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
                {detail.shortAnswer}
              </p>
            </section>

            <section className="rounded-2xl border border-border/60 bg-card p-6 sm:p-8">
              <h2 className="font-heading text-2xl font-bold tracking-tight text-foreground">
                {detail.prijsLabel}
              </h2>
              <p className="mt-3 font-heading text-3xl font-bold leading-tight tracking-tight text-primary sm:text-4xl">
                {detail.prijs}
              </p>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                {detail.prijsNoot}
              </p>

              <h3 className="mt-6 font-heading text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Prijs hangt af van
              </h3>
              <ul className="mt-3 space-y-2.5 text-sm leading-relaxed text-foreground/80">
                {detail.factoren.map((factor) => (
                  <li key={factor} className="flex items-start gap-2.5">
                    <CheckCircle
                      className="mt-0.5 h-4 w-4 shrink-0 text-primary"
                      aria-hidden="true"
                    />
                    <span className="min-w-0">{factor}</span>
                  </li>
                ))}
              </ul>

              <p className="mt-5 text-xs leading-relaxed text-muted-foreground">
                {kostenDisclaimer}
              </p>

              <div className="mt-6">
                <Link
                  to="/contact"
                  className="inline-flex min-h-12 items-center justify-center gap-2 rounded-md bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground transition-colors hover:bg-primary/90"
                >
                  Vraag een prijs op maat aan
                  <ArrowRight className="h-4 w-4" aria-hidden="true" />
                </Link>
              </div>
            </section>

            <section>
              <h2 className="font-heading text-2xl font-bold tracking-tight text-foreground">
                {detail.tabelTitel}
              </h2>
              <div className="mt-6 overflow-hidden rounded-2xl border border-border/60 bg-card">
                <ul className="divide-y divide-border/60">
                  {detail.tabel.map((rij) => (
                    <li key={rij.label} className="p-4 sm:p-5">
                      <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
                        <span className="font-heading text-sm font-semibold text-foreground sm:text-base">
                          {rij.label}
                        </span>
                        <span className="font-heading text-sm font-bold text-primary sm:text-base">
                          {rij.prijs}
                        </span>
                      </div>
                      <p className="mt-1 text-xs leading-relaxed text-muted-foreground sm:text-sm">
                        {rij.noot}
                      </p>
                    </li>
                  ))}
                </ul>
              </div>
            </section>

            <section>
              <h2 className="font-heading text-2xl font-bold tracking-tight text-foreground">
                Tips om te besparen
              </h2>
              <ul className="mt-4 space-y-3">
                {detail.tips.map((tip) => (
                  <li
                    key={tip}
                    className="flex gap-3 text-sm leading-relaxed text-muted-foreground"
                  >
                    <span
                      aria-hidden="true"
                      className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-primary"
                    />
                    <span>{tip}</span>
                  </li>
                ))}
              </ul>
            </section>

            <section>
              <h2 className="font-heading text-2xl font-bold tracking-tight text-foreground">
                Veelgestelde vragen
              </h2>
              <div className="mt-6 space-y-4">
                {detail.faq.map((item) => (
                  <div
                    key={item.vraag}
                    className="rounded-xl border border-border/60 bg-card p-4 sm:p-5"
                  >
                    <h3 className="font-heading text-base font-semibold text-foreground">
                      {item.vraag}
                    </h3>
                    <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                      {item.antwoord}
                    </p>
                  </div>
                ))}
              </div>
            </section>

            <section>
              <h2 className="font-heading text-xl font-semibold text-foreground">
                Andere kostengidsen
              </h2>
              <div className="mt-4 flex flex-col gap-2 text-sm font-semibold">
                <Link
                  to="/aanbouw-kosten"
                  className="inline-flex items-center gap-1 text-primary hover:text-primary/80"
                >
                  Aanbouw kosten: complete gids
                  <ArrowRight className="h-4 w-4" aria-hidden="true" />
                </Link>
                {detail.slug !== "uitbouw-kosten-per-m2" && (
                  <Link
                    to="/uitbouw-kosten-per-m2"
                    className="inline-flex items-center gap-1 text-primary hover:text-primary/80"
                  >
                    Uitbouw kosten per m²
                    <ArrowRight className="h-4 w-4" aria-hidden="true" />
                  </Link>
                )}
                {detail.slug !== "casco-aanbouw-kosten" && (
                  <Link
                    to="/casco-aanbouw-kosten"
                    className="inline-flex items-center gap-1 text-primary hover:text-primary/80"
                  >
                    Casco aanbouw kosten
                    <ArrowRight className="h-4 w-4" aria-hidden="true" />
                  </Link>
                )}
                {detail.slug !== "aanbouw-2-verdiepingen-kosten" && (
                  <Link
                    to="/aanbouw-2-verdiepingen-kosten"
                    className="inline-flex items-center gap-1 text-primary hover:text-primary/80"
                  >
                    Aanbouw over 2 verdiepingen
                    <ArrowRight className="h-4 w-4" aria-hidden="true" />
                  </Link>
                )}
              </div>
            </section>
          </div>
        </div>
      </div>

      <CtaBand />
    </>
  );
}

export function kostenDetailHead(detail: KostenDetail) {
  const path = `/${detail.slug}`;
  return {
    meta: [
      { title: detail.title },
      { name: "description", content: detail.description },
      { property: "og:title", content: detail.socialTitle },
      { name: "twitter:title", content: detail.socialTitle },
      { property: "og:description", content: detail.socialDescription },
      { name: "twitter:description", content: detail.socialDescription },
      { property: "og:type", content: "article" },
      { property: "og:url", content: `${SITE_URL}${path}` },
      ...ogImageMeta(ogImageDefault, `${detail.h1} — Fedor Timmerwerken`),
    ],
    links: [{ rel: "canonical", href: `${SITE_URL}${path}` }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Article",
          headline: detail.h1,
          description: detail.description,
          inLanguage: "nl-NL",
          image: ogImageDefault,
          author: { "@type": "Organization", name: "Fedor Timmerwerken" },
          publisher: {
            "@type": "Organization",
            name: "Fedor Timmerwerken",
            logo: { "@type": "ImageObject", url: ogImageDefault },
          },
          mainEntityOfPage: { "@type": "WebPage", "@id": `${SITE_URL}${path}` },
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: detail.faq.map((item) => ({
            "@type": "Question",
            name: item.vraag,
            acceptedAnswer: { "@type": "Answer", text: item.antwoord },
          })),
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_URL}/` },
            { "@type": "ListItem", position: 2, name: "Kosten", item: `${SITE_URL}/kosten` },
            {
              "@type": "ListItem",
              position: 3,
              name: "Aanbouw kosten",
              item: `${SITE_URL}/aanbouw-kosten`,
            },
            { "@type": "ListItem", position: 4, name: detail.h1, item: `${SITE_URL}${path}` },
          ],
        }),
      },
    ],
  };
}
